<template>
  <div class="profileBanner" data-test-id="bandProfileBanner">
    <ImageCropper
      ref="cropper"
      target="banner"
      :aspect-ratio-x="128"
      :aspect-ratio-y="29"
      :modal-title="$t('band.profile.slug.edit.picture01')"
    >
      <template #actions>
        <div class="tw-grid-6 tw-grid tw-grid-cols-1 tw-gap-sm tw-p-2xl">
          <BtnGhost @click="addPicture">
            {{ $t('shared.profile.banner00') }}
          </BtnGhost>
          <BtnOrange @click="confirmCrop">
            {{ $t('shared.profile.banner01') }}
          </BtnOrange>
        </div>
      </template>
    </ImageCropper>
    <transition>
      <img
        v-if="band.has_banner || picture"
        class="tw-h-full tw-w-full tw-object-cover tw-object-center"
        :src="bannerUrl"
        alt=""
      />
    </transition>
    <div v-if="canEdit" class="editPrompt" @click="addPicture">
      <i class="fas fa-pen" />
    </div>
  </div>
</template>

<script lang="ts">
import * as Sentry from '@sentry/nuxt'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import ImageCropper from '~/components/images/cropper.vue'
import { BtnGhost, BtnOrange } from '~/components/ui/Buttons'

import { useUserStore } from '~/stores/user'
import { useUserBandSetStore } from '~/stores/userBandSet'

import pictureBuildHelper from '~/helpers/images/picture_build'

import type { ImageCropperUploadResponse } from '~/components/images/cropper.vue'
import type Band from '~/types/band'
import type { PropType } from 'vue'

export default defineComponent({
  components: {
    BtnGhost,
    BtnOrange,
    ImageCropper,
  },
  props: {
    band: {
      type: Object as PropType<Band>,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const cropper = ref<InstanceType<typeof ImageCropper> | null>(null)
    const { timestamp } = useGetServerSyncedTimestamp('bandProfileBanner')

    return { cropper, timestamp }
  },
  data() {
    return {
      picture: null as null | string,
    }
  },
  computed: {
    ...mapState(useUserStore, { USER_ID: 'id' }),
    bannerUrl(): string {
      if (this.picture !== null) {
        return this.picture
      } else {
        return (
          pictureBuildHelper({
            kind: 'band',
            slug: this.band.slug,
            target: 'banner',
            size: '1280_290',
          }) + `?ts=${this.timestamp}`
        )
      }
    },
  },
  methods: {
    ...mapActions(useUserBandSetStore, ['MERGE_BAND_PATCH']),
    addPicture() {
      this.cropper?.addPicture()
    },
    confirmCrop() {
      if (!this.cropper) return

      this.picture = this.cropper.getCroppedCanvas() || null
      this.cropper.close()
      this.cropper
        .upload({
          id: this.USER_ID,
          slug: this.band.slug,
          kind: 'band',
        })
        .then((response: ImageCropperUploadResponse) => {
          this.picture = response.picture
          this.MERGE_BAND_PATCH({
            band_patch: { has_banner: true },
            band_id: this.band.id,
          })
        })
        .catch((error: Error) => {
          Sentry.captureException(error)
        })
    },
  },
})
</script>

<style lang="scss" scoped>
.profileBanner {
  @apply tw-relative tw-w-full;
  height: 290px;

  &::after {
    @apply tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full tw-opacity-50;
    content: '';
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), #000);
  }

  &:hover {
    .editPrompt {
      @apply tw-opacity-100;
    }
  }
}

.editPrompt {
  @apply tw-absolute tw-z-10 tw-flex tw-max-w-2 tw-cursor-pointer tw-justify-end tw-font-medium tw-text-white tw-opacity-0 tw-transition-opacity tw-duration-150 tw-ease-in-out;
  right: 24px;
  bottom: 24px;
}

.btn {
  @apply tw-w-full;
}
</style>
